import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import InvoiceApi from '@infrastructure/invoice-api';
import { useActorBoundary } from '@application/common/hooks/useActorBoundary';
import { useBoundRequest } from '@application/common/hooks/useBoundRequest';
import { useAppDispatch } from '@application/store/useStore';
import { InvoiceStatus } from '@application/common/enums/invoice-status';
import { downloadCOCDocumentAsync } from '@application/features/freelancer/freelancerThunks';
import { openOrDownloadPdf } from '@application/common/helpers/file-helper';
import { InvoicePaymentSummary } from '../../../freelancer/invoice/InvoicePaymentSummary';
// Optional source review for financing; ordinary administration does not pass through this dialog.
export function InvoiceDialog({ open, handleClose, invoiceId }) {
    return open ? _jsx(ReviewContext, { invoiceId: invoiceId, onClose: handleClose }, invoiceId) : null;
}
function ReviewContext({ invoiceId, onClose }) {
    const { t, i18n } = useTranslation('faqtoring');
    const actor = useActorBoundary();
    const dispatch = useAppDispatch();
    const [busy, setBusy] = useState(false);
    const updating = useRef(false);
    const [error, setError] = useState(false);
    const [reason, setReason] = useState('');
    const detail = useBoundRequest(`${actor.actor}:${invoiceId}`, () => actor.read(async () => {
        const response = await InvoiceApi.getByInvoiceId(invoiceId);
        if (response.data.id !== invoiceId)
            throw new Error('Invoice context mismatch');
        return response.data;
    }));
    const invoice = detail.data;
    const historical = invoice?.summary?.canReviewHistoricalInvoice === true;
    const canReview = invoice?.summary?.canReviewForFinancing === true || historical;
    const update = async (status) => {
        if (updating.current || !canReview || !actor.check())
            return;
        updating.current = true;
        setBusy(true);
        setError(false);
        try {
            await actor.read(() => InvoiceApi.UpdateInvoiceStatus({ id: invoiceId, status, reason }));
            onClose();
        }
        catch {
            setError(true);
        }
        finally {
            updating.current = false;
            setBusy(false);
        }
    };
    const download = async (folderName, fileName) => {
        if (!folderName || !fileName)
            return;
        try {
            const response = await actor.read(() => dispatch(downloadCOCDocumentAsync({ folderName, fileName })).unwrap());
            if (actor.check())
                openOrDownloadPdf(response.file, fileName, fileName);
        }
        catch {
            setError(true);
        }
    };
    return _jsxs(Dialog, { open: true, fullWidth: true, maxWidth: 'sm', onClose: () => { if (!busy)
            onClose(); }, children: [_jsxs(DialogTitle, { children: [t(historical ? 'reviewHistoricalInvoice' : 'reviewForFinancing'), invoice ? ` · ${invoice.invoiceNo}` : ''] }), _jsx(DialogContent, { children: actor.changed ? _jsx(Alert, { severity: 'warning', children: t('accountChanged') }) : _jsxs(_Fragment, { children: [_jsx(Alert, { severity: 'info', children: t(historical ? 'historicalReviewSourceHint' : 'financingReviewHint') }), detail.status === 'loading' && _jsx(Typography, { role: 'status', children: t('loading') }), (error || detail.status === 'error') && _jsx(Alert, { severity: 'error', action: _jsx(Button, { disabled: busy, onClick: () => { setError(false); detail.reload(); }, children: t('refresh') }), children: t('actionError') }), invoice && _jsxs(_Fragment, { children: [_jsxs(Box, { sx: { my: 2 }, children: [_jsxs(Typography, { children: [invoice.freelancerName, " \u00B7 ", invoice.clientName] }), _jsxs(Typography, { children: [t('invoiceGross'), ": ", new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' }).format(invoice.amountInclBTW)] })] }), _jsx(InvoicePaymentSummary, { summary: invoice.summary }), invoice.invoiceDocuments.map(document => _jsxs(Button, { onClick: () => void download(document.folderName, document.name), children: [t('download'), ": ", document.name] }, document.invoiceDocumentId || document.name)), _jsx(TextField, { fullWidth: true, multiline: true, label: t('correctionReason'), value: reason, onChange: event => setReason(event.target.value), disabled: busy, sx: { mt: 2 } })] })] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { disabled: busy, onClick: onClose, children: t('close') }), _jsx(Button, { disabled: busy || actor.changed || !reason.trim() || !canReview, onClick: () => void update(InvoiceStatus.Disapproved), children: t('requestCorrection') }), _jsx(Button, { disabled: busy || actor.changed || !canReview, onClick: () => void update(InvoiceStatus.Approved), children: t(historical ? 'approveHistoricalInvoice' : 'approveSourceForFinancing') })] })] });
}
